import { createContext } from 'react';
import { IResourceCampaign } from '../customer/components/Common';

export enum Pick {
  PICK2 = 'pick2',
  PICK3 = 'pick3',
  PICK4 = 'pick4',
  PICK5 = 'pick5',
  POWERBALL = 'powerball',
  RABBIT = 'rabbit',
  PICKIFY = 'pickify',
  TRIZ = 'triz',
}

export enum TicketType {
  BOXED = 'boxed',
  STRAIGHT = 'straight',
}

export enum GameType {
  PICK2 = 'LotteryPick2',
  PICK3 = 'LotteryPick3',
  PICK4 = 'LotteryPick4',
  PICK5 = 'LotteryPick5',
}

export interface INumber {
  value: string;
  isNew: boolean;
  willBeRemoved: boolean;
  newIndex: number;
}

export interface IDomain {
  url: string;
  isEnabled: boolean;
}

export interface IDrawing {
  uniqueDrawingId: number;
  description: string;
  state: string;
  stateName: string;
  scheduledHour: string;
  drawDate: string;
  gameType: GameType;
  isEnabled: boolean;
  anyFakeTicketNumber: boolean;
  domains: IDomain[];
  url: string;
  noAction: boolean;
  hasFireBall: boolean;
}

enum TrizGameType {
  Triz = 'LotteryTriz',
}

export interface ITrizDrawing extends Omit<IDrawing, 'gameType'> {
  gameType: TrizGameType;
}

export enum TrizPick {
  P2 = 'pick2',
  P3 = 'pick3',
  P4 = 'pick4',
  P5 = 'pick5',
}

export interface TrizCombo {
  id: string;
  number: string;
  splittedNumbers: string[];
  pick: TrizPick;
}

export interface DrawingAvailability {
  description: string;
  drawDate: string;
  withFireBall: boolean;
  unavailableNumbers: string[];
  availableNumbers: string[];
  issued: string | null;
}

export interface IPurchase {
  numbers: INumber[];
  pick: Pick;
  dates: Date[];
  drawings: IDrawing[];
  trizDrawings: ITrizDrawing[];
  trizCombos: TrizCombo[];
  trizDates: Date[];
  drawingsAvailability?: DrawingAvailability[];
  amount: number;
  multiplier: number;
  type: TicketType;
  timeDifference: number;
  timeDifferenceTriz: number;
  showRibbon: boolean;
  offDrawingsConfirmed: boolean;
  hasLoaded: boolean;
}

export interface IPlayerInformation {
  avatar: string;
  nickname: string;
}

export interface BalanceResponse {
  accounts: any[];
  balance: number;
  balanceFormatted: string;
  changePercentage: number;
  code: string;
  locked: number;
  lockedFormatted: string;
  type: string;
  visible?: boolean;
  isRewardCurrency?: boolean;
  isStandardCurrency?: boolean;
  orderCurrencyType?: string;
  unicode: string;
}

interface ISpinWheels {
  level: number;
  title: string;
  segments: string[];
  streakGoal?: number;
  percentage?: string;
}

interface IStages {
  level: number;
  title: string;
  percentage?: string;
}
export interface ICampaignHSeason {
  maxStreakDays: number;
  maxLevels: number;
  hasCampaignStart: boolean;
  spinWheel: ISpinWheels[];
  stages?: IStages[];
  resources: { key: string; url: string }[];
  rewardType?: string;
  // ActionerStreak+Blackjack: el arreglo por-nivel llega bajo la key `tiers`
  // (no `spinWheel`). Se mapea a `spinWheel` en getCampaignInformation para
  // reusar los mismos componentes de progresión por nivel que SpinWheel.
  tiers?: {
    level: number;
    streakGoal?: number;
    basePrize?: number;
    consolationPrize?: number;
    title?: string;
  }[];
}
export interface ICampaignHSeasonStatus {
  currentStreakGoal: number;
  currentLevel: number;
  isParticipating: boolean;
  timeForNextStreak: number;
  availableSpinsByLevel?: { turns: number; level: number }[];
  remainingAmount?: number;
  canPlayerClaim?: boolean;
  startClaimDate?: string;
  endClaimDate?: string;
  showClosestDate?: boolean;
  allowedDaysOfWeek?: string[];
  playerDaysOfWeek?: string[];
  playerAmountGoal?: number;
  currentStreakPlayerAmount?: number;
  // Blackjack (modelo lazy): partidas pendientes de reclamar y tier vigente.
  pendingGames?: number;
  currentTierLevel?: number;
  totalTiers?: number;
  currentTierBasePrize?: number;
  currentTierConsolationPrize?: number;
}
export interface IBalance {
  balanceFormatted: string;
  balance: number;
  alreadySearch: boolean;
  hasErrorBalance: boolean;
  currencyCode: string;
  isOpenBalanceModal: boolean;
  orderCurrencyType?: string;
  unicode: string;
}

export interface ICampaignSelect {
  promotionNumber: number;
  campaignName: string;
  scaleType: string;
  rewardType: string;
  resources?: IResourceCampaign[];
  urlImage?: any;
}

interface ICurrency {
  balance: number;
  currencyCode: string;
  orderCurrencyType?: string;
  unicode: string;
}

export interface IDefaultCurrencies {
  standardCurrency: string;
  rewardCurrency: string;
}

export enum Theme {
  Default = 'default',
  Halloween2024 = 'halloween2024',
}

type IPlayerContext = {
  purchase: IPurchase;
  setPurchase: (newPurchase: IPurchase) => void;
  playerInformation: IPlayerInformation;
  setPlayerInformation: (information: IPlayerInformation) => void;
  balanceContext: IBalance;
  refreshBalance: () => Promise<number>;
  buyAgain: (newPurchase: IPurchase) => void;
  isSplashActive: boolean;
  setIsCurrentSplashView: (isActive: boolean) => void;
  balanceList: BalanceResponse[];
  setCurrencyAndBalance: (currency: ICurrency) => void;
  toggleBalanceModal: () => void;
  campaignInformation: ICampaignHSeason;
  campaignStatus: ICampaignHSeasonStatus;
  isOpenPromoSeasonModal: boolean;
  togglePromoSessionModal: () => any;
  isOpenSpinModal: boolean;
  toggleSpinModal: () => any;
  isOpenCampaignStatusModal: boolean;
  toggleCampaignStatusModal: () => void;
  isOpenCongratulationCampaignModal: boolean;
  toggleCongratulationCampaignModal: () => void;
  setCongratulationValue: (value: string) => void;
  congratulationValue: string;
  isOpenLevelUpModal: boolean;
  toggleLevelUpModal: () => void;
  isOpenLoseStreakModal: boolean;
  toggleLoseStreakModal: () => void;
  getCampaignStatus: (id?: number) => void;
  getCampaignInformation: (id?: number) => void;
  addCampaignId: (campaign: ICampaignSelect) => void;
  campaignIdSelected: ICampaignSelect;
  shareData?: {
    files: any[];
    title: string;
    text: string;
  };
  isOpenCampaignOptionModal: boolean;
  toggleCampaignOptionsModal: () => void;
  campaignList: ICampaignSelect[];
  setCampaignList: (campaignList: ICampaignSelect[]) => void;
  localization: string | null;
  theme: Theme;
};

const now = new Date();

export const defaultPurchaseValue: IPurchase = {
  numbers: [],
  pick: Pick.PICK3,
  dates: [new Date(now.getFullYear(), now.getMonth(), now.getDate())],
  drawings: [],
  trizDrawings: [],
  trizCombos: [],
  trizDates: [new Date(now.getFullYear(), now.getMonth(), now.getDate())],
  drawingsAvailability: [],
  amount: 1,
  multiplier: 1,
  type: TicketType.STRAIGHT,
  timeDifference: 0,
  timeDifferenceTriz: 0,
  showRibbon: false,
  offDrawingsConfirmed: false,
  hasLoaded: false,
};

const defaultPlayerInformation: IPlayerInformation = {
  avatar: '',
  nickname: '',
};

export const defaultBalance: IBalance = {
  balanceFormatted: '',
  balance: 0,
  alreadySearch: false,
  hasErrorBalance: false,
  currencyCode: '',
  isOpenBalanceModal: false,
  orderCurrencyType: '',
  unicode: '',
};

export const defaultWheelsHarvestSeason: ICampaignHSeason = {
  maxStreakDays: 0,
  maxLevels: 0,
  hasCampaignStart: false,
  spinWheel: [],
  resources: [],
  stages: [],
};

export const defaultCampaignStatus: ICampaignHSeasonStatus = {
  currentStreakGoal: 0,
  currentLevel: 0,
  isParticipating: false,
  timeForNextStreak: 0,
  availableSpinsByLevel: [],
  remainingAmount: 0,
  canPlayerClaim: false,
  showClosestDate: false,
  startClaimDate: '',
  endClaimDate: '',
  playerDaysOfWeek: [],
  allowedDaysOfWeek: [],
  playerAmountGoal: 0,
  currentStreakPlayerAmount: 0,
  pendingGames: 0,
  currentTierLevel: 0,
  totalTiers: 1,
  currentTierBasePrize: 0,
  currentTierConsolationPrize: 0,
};

export const PlayerContext = createContext<IPlayerContext>({
  purchase: defaultPurchaseValue,
  setPurchase: () => {},
  playerInformation: defaultPlayerInformation,
  setPlayerInformation: () => {},
  balanceContext: defaultBalance,
  refreshBalance: () => Promise.resolve(0),
  buyAgain: () => {},
  isSplashActive: false,
  setIsCurrentSplashView: () => {},
  balanceList: [],
  setCurrencyAndBalance: () => {},
  toggleBalanceModal: () => {},
  campaignInformation: defaultWheelsHarvestSeason,
  campaignStatus: defaultCampaignStatus,
  isOpenPromoSeasonModal: false,
  togglePromoSessionModal: () => {},
  isOpenSpinModal: false,
  toggleSpinModal: () => {},
  isOpenCampaignStatusModal: false,
  toggleCampaignStatusModal: () => {},
  isOpenCongratulationCampaignModal: false,
  toggleCongratulationCampaignModal: () => {},
  congratulationValue: '',
  setCongratulationValue: (value: string) => {},
  isOpenLevelUpModal: false,
  toggleLevelUpModal: () => {},
  isOpenLoseStreakModal: false,
  toggleLoseStreakModal: () => {},
  getCampaignStatus: (id?: number) => {},
  getCampaignInformation: (id?: number) => {},
  addCampaignId: (campaign: ICampaignSelect) => {},
  campaignIdSelected: {
    promotionNumber: -1,
    campaignName: '',
    scaleType: '',
    rewardType: '',
    resources: [],
  },
  isOpenCampaignOptionModal: false,
  toggleCampaignOptionsModal: () => {},
  campaignList: [],
  setCampaignList: (campaignList: ICampaignSelect[]) => {},
  localization: null,
  theme: Theme.Default,
});
